import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { ProductionLineConfig } from '../types/industrialCost';
import {
  getIndustrialCostProductionCapacityIntelligence,
  type ProductionCapacityMachine,
  type ProductionPressureWindow,
  type ProductionScheduleOrder,
  type ProductionScheduleScenario,
} from '../services/productionCapacityService';

import { esNumberFormat } from '../utils/numberFormat';
type Props = {
  referenceDate: string;
  configuredLines: ProductionLineConfig[];
};

const panelSx = {
  bgcolor: '#0b1220',
  border: '1px solid rgba(148,163,184,0.18)',
  borderRadius: 2.5,
};

const num = (value: number) =>
  esNumberFormat({
    maximumFractionDigits: 2,
  }).format(value);

const displayDate = (value: string | null | undefined) =>
  value
    ? new Intl.DateTimeFormat('es-ES').format(
        new Date(`${value}T00:00:00`),
      )
    : '—';

function normalizeLineKey(value: string | null | undefined) {
  return (value || 'Sin línea')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

// IC-INT-04: lectura operativa de la simulación
function scenarioBacklogCompletionDate(
  scenario: ProductionScheduleScenario | null,
): string | null {
  const overdue = (scenario?.sequence ?? []).filter(
    (order) => order.status === 'already_overdue',
  );

  return overdue.length > 0
    ? overdue[overdue.length - 1].completion_date
    : null;
}

function scheduleDecision(machine: ProductionCapacityMachine) {
  const scheduling = machine.scheduling;
  const proposed = scheduling.proposed;
  const saved =
    scheduling.improvement?.saved_future_late_orders ?? 0;

  if (machine.status === 'no_capacity') {
    return {
      label: 'Sin capacidad configurada',
      color: 'error' as const,
    };
  }

  if (!scheduling.is_available || !proposed) {
    return {
      label: 'No evaluable',
      color: 'warning' as const,
    };
  }

  if (proposed.future_late_orders > 0) {
    return {
      label: `${proposed.future_late_orders} futura${
        proposed.future_late_orders === 1 ? '' : 's'
      } aún tarde`,
      color: 'error' as const,
    };
  }

  if (saved > 0) {
    return {
      label: `${saved} futura${saved === 1 ? '' : 's'} protegida${
        saved === 1 ? '' : 's'
      }`,
      color: 'success' as const,
    };
  }

  if (machine.overdue_orders > 0) {
    return {
      label: 'Sin nuevas tardanzas',
      color: 'info' as const,
    };
  }

  return {
    label: 'Todo cabe en plazo',
    color: 'success' as const,
  };
}

function orderResult(order: ProductionScheduleOrder) {
  if (order.status === 'already_overdue') {
    return {
      label: 'Ya vencida',
      color: 'warning' as const,
    };
  }

  if (order.status === 'late') {
    return {
      label: `Tarde +${num(order.tardiness_hours)} h`,
      color: 'error' as const,
    };
  }

  return {
    label: 'En plazo',
    color: 'success' as const,
  };
}

// IC-INT-07: lectura principal compacta.
function getPressurePresentation(machine: ProductionCapacityMachine) {
  const windows = machine.pressure_windows;
  const overloaded = windows.filter(
    (window) => window.peak_deficit_hours > 0,
  );

  if (overloaded.length === 0) {
    return {
      label: 'Sin presión',
      detail: 'Sin déficit en las próximas 4 semanas.',
      recoveryDate: null as string | null,
      hasPressure: false,
    };
  }

  const lastOverloadedIndex = Math.max(
    ...overloaded.map((window) => window.index),
  );

  const recoveryWindow = windows.find(
    (window) =>
      window.index > lastOverloadedIndex
      && window.peak_deficit_hours <= 0,
  );

  if (overloaded.length === windows.length) {
    return {
      label: 'Sobrecarga persistente',
      detail: 'No recupera margen dentro del horizonte analizado.',
      recoveryDate: null as string | null,
      hasPressure: true,
    };
  }

  return {
    label:
      overloaded.length === 1
        ? 'Presión puntual'
        : 'Presión temporal',
    detail:
      overloaded.length === 1
        ? 'Déficit concentrado en una semana.'
        : 'Déficit durante varias semanas.',
    recoveryDate: recoveryWindow?.start_date ?? null,
    hasPressure: true,
  };
}

function PressureTimeline({
  windows,
}: {
  windows: ProductionPressureWindow[];
}) {
  return (
    <Stack direction="row" spacing={0.6} sx={{ minWidth: 340 }}>
      {windows.map((window) => {
        const overloaded = window.peak_deficit_hours > 0;

        const title = overloaded
          ? `Semana ${window.index}: faltan hasta ${num(
              window.peak_deficit_hours,
            )} h`
          : `Semana ${window.index}: margen mínimo +${num(
              Math.max(window.minimum_margin_hours, 0),
            )} h`;

        return (
          <Box
            key={window.index}
            title={`${title} · ${displayDate(
              window.start_date,
            )}–${displayDate(window.end_date)}`}
            sx={{
              flex: 1,
              minWidth: 70,
              px: 0.9,
              py: 0.75,
              borderRadius: 1.4,
              border: '1px solid',
              borderColor: overloaded
                ? 'rgba(248,113,113,0.55)'
                : 'rgba(148,163,184,0.18)',
              bgcolor: overloaded
                ? 'rgba(127,29,29,0.18)'
                : 'rgba(30,41,59,0.26)',
            }}
          >
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.66rem',
                fontWeight: 800,
              }}
            >
              S{window.index}
            </Typography>

            <Typography
              sx={{
                color: overloaded ? '#fca5a5' : '#cbd5e1',
                fontSize: '0.76rem',
                fontWeight: 900,
              }}
            >
              {overloaded ? 'Déficit' : 'Margen'}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function ScenarioMetric({
  title,
  lateOrders,
  tardinessHours,
  helper,
  accent,
}: {
  title: string;
  lateOrders: number | null;
  tardinessHours: number | null;
  helper: string;
  accent?: 'success' | 'warning';
}) {
  const color = accent === 'success'
    ? '#86efac'
    : accent === 'warning'
      ? '#fbbf24'
      : '#f8fafc';

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 1.4,
        minHeight: 104,
      }}
    >
      <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
        {title}
      </Typography>
      <Typography sx={{ color, fontSize: '1.32rem', fontWeight: 900 }}>
        {lateOrders === null ? '—' : `${lateOrders} OF futuras tarde`}
      </Typography>
      <Typography sx={{ color: '#cbd5e1', fontSize: '0.78rem', fontWeight: 800 }}>
        {tardinessHours === null
          ? 'Sin cálculo'
          : `${num(tardinessHours)} h de retraso futuro acumulado`}
      </Typography>
      <Typography variant="caption" sx={{ color: '#64748b' }}>
        {helper}
      </Typography>
    </Paper>
  );
}

export default function IndustrialCostProductionInsightsPanel({
  referenceDate,
  configuredLines,
}: Props) {
  const [expandedMachine, setExpandedMachine] = useState<string | null>(null);
  const [expandedPressureMachine, setExpandedPressureMachine] = useState<
    string | null
  >(null);
  const [showSequenceSimulator, setShowSequenceSimulator] = useState(false);

  const capacityQuery = useQuery({
    queryKey: [
      'industrial-cost-production-capacity-intelligence',
      referenceDate,
    ],
    queryFn: () =>
      getIndustrialCostProductionCapacityIntelligence(referenceDate),
    enabled: Boolean(referenceDate),
    staleTime: 60_000,
    retry: false,
  });

  const configuredLineOrder = useMemo(() => {
    const result = new Map<string, number>();

    [...configuredLines]
      .filter((line) => line.is_active)
      .sort((a, b) => a.display_order - b.display_order)
      .forEach((line, index) => {
        result.set(normalizeLineKey(line.code), index);
        result.set(normalizeLineKey(line.name), index);
      });

    return result;
  }, [configuredLines]);

  const configuredLineByKey = useMemo(() => {
    const result = new Map<string, ProductionLineConfig>();

    configuredLines
      .filter((line) => line.is_active)
      .forEach((line) => {
        [
          line.code,
          line.name,
          ...(line.aliases ?? []),
        ].forEach((value) => {
          const key = normalizeLineKey(value);
          if (!result.has(key)) {
            result.set(key, line);
          }
        });
      });

    return result;
  }, [configuredLines]);

  const sortedMachines = useMemo(() => {
    return [...(capacityQuery.data?.machines ?? [])].sort((a, b) => {
      const aOrder =
        configuredLineOrder.get(normalizeLineKey(a.machine))
        ?? Number.MAX_SAFE_INTEGER;
      const bOrder =
        configuredLineOrder.get(normalizeLineKey(b.machine))
        ?? Number.MAX_SAFE_INTEGER;

      return (
        aOrder - bOrder
        || a.machine.localeCompare(b.machine, 'es')
      );
    });
  }, [capacityQuery.data?.machines, configuredLineOrder]);

  if (capacityQuery.isLoading) {
    return (
      <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <CircularProgress size={18} />
          <Typography>Analizando presión de capacidad…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (capacityQuery.isError) {
    return (
      <Alert severity="warning" variant="outlined">
        No se pudo calcular la presión de capacidad. Revisa turnos,
        calendario y permisos de planificación.
      </Alert>
    );
  }

  const capacity = capacityQuery.data;

  if (!capacity?.snapshot) {
    return (
      <Alert severity="info" variant="outlined">
        Todavía no existe un snapshot publicado de órdenes pendientes para
        analizar.
      </Alert>
    );
  }

  const coveragePct = capacity.summary.orders_count > 0
    ? (
      capacity.summary.known_hours_orders
      / capacity.summary.orders_count
      * 100
    )
    : 0;

  const proposedFutureLateOrders = sortedMachines.reduce(
    (sum, machine) =>
      sum + (machine.scheduling.proposed?.future_late_orders ?? 0),
    0,
  );

  const baselineFutureLateOrders = sortedMachines.reduce(
    (sum, machine) =>
      sum + (machine.scheduling.baseline?.future_late_orders ?? 0),
    0,
  );

  const calculableOrders = sortedMachines.reduce(
    (sum, machine) => sum + machine.scheduling.eligible_orders,
    0,
  );

  const calculableBacklogOrders = sortedMachines.reduce(
    (sum, machine) => sum + machine.scheduling.already_overdue_orders,
    0,
  );

  const calculableFutureOrders = Math.max(
    calculableOrders - calculableBacklogOrders,
    0,
  );

  const savedFutureOrders = Math.max(
    baselineFutureLateOrders - proposedFutureLateOrders,
    0,
  );

  const bufferDays =
    capacity.calendar.manufacturing_deadline_buffer_working_days;

  return (
    <Paper elevation={0} sx={{ ...panelSx, p: 2.1 }}>
      <Stack spacing={1.8}>
        <Box>
          <Typography
            sx={{
              color: '#7dd3fc',
              fontWeight: 900,
              fontSize: '1rem',
            }}
          >
            Inteligencia de producción · Presión temporal
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 0.25 }}>
            Detecta cuándo la carga que debe estar terminada supera la
            capacidad disponible de cada línea y señala dónde existe margen
            simultáneo para estudiar alternativas.
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.4 }}>
            Fecha de análisis: {displayDate(referenceDate)} · Snapshot:{' '}
            {new Date(capacity.snapshot.published_at).toLocaleString('es-ES')}.
          </Typography>
        </Box>

        <Alert
          severity={
            capacity.summary.machines_with_pressure_overload > 0
              ? 'warning'
              : 'success'
          }
          variant="outlined"
        >
          <strong>Lectura rápida.</strong>{' '}
          {capacity.summary.machines_with_pressure_overload > 0 ? (
            <>
              Hay{' '}
              <strong>
                {capacity.summary.machines_with_pressure_overload} líneas
              </strong>{' '}
              con algún déficit conocido dentro de las próximas{' '}
              <strong>
                {capacity.summary.pressure_horizon_weeks} semanas productivas
              </strong>.
              {' '}El mayor déficit es de{' '}
              <strong>
                {num(capacity.summary.maximum_pressure_deficit_hours)} h
              </strong>
              {capacity.summary.maximum_pressure_deficit_machine
                ? ` en ${capacity.summary.maximum_pressure_deficit_machine}`
                : ''}
              .
            </>
          ) : (
            <>
              La carga conocida cabe dentro de la capacidad de todas las
              líneas durante las próximas{' '}
              <strong>
                {capacity.summary.pressure_horizon_weeks} semanas productivas
              </strong>.
            </>
          )}
        </Alert>

        <Alert severity="info" variant="outlined">
          Una expedición debe quedar fabricada{' '}
          <strong>
            {bufferDays} día{bufferDays === 1 ? '' : 's'} productivo
            {bufferDays === 1 ? '' : 's'} antes
          </strong>.
          {' '}Cobertura actual: <strong>{num(coveragePct)} %</strong>
          {' '}({capacity.summary.missing_hours_orders} OF sin horas).
          {bufferDays === 1
            ? ' Ejemplo: una expedición del lunes debe quedar terminada el viernes anterior con calendario L–V.'
            : ''}
        </Alert>

        {capacity.calendar.warnings.length > 0 && (
          <Alert severity="warning" variant="outlined">
            {capacity.calendar.warnings.join(' ')}
          </Alert>
        )}

        <Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Presión por línea
          </Typography>
          <Typography
            sx={{ color: '#94a3b8', fontSize: '0.76rem', mt: 0.25 }}
          >
            Lectura rápida por línea: estado actual, evolución durante cuatro
            semanas y si existe alguna línea del mismo grupo con margen para
            revisar una posible redistribución.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            {
              title: 'Líneas a revisar',
              value: `${capacity.summary.machines_with_pressure_overload}/${capacity.summary.machines_count}`,
              helper: capacity.summary.earliest_pressure_overload_date
                ? `Primera presión: ${displayDate(
                    capacity.summary.earliest_pressure_overload_date,
                  )}`
                : 'Sin presión conocida',
            },
            {
              title: 'Mayor presión',
              value: `${num(
                capacity.summary.maximum_pressure_deficit_hours,
              )} h`,
              helper:
                capacity.summary.maximum_pressure_deficit_machine
                  ? `En ${capacity.summary.maximum_pressure_deficit_machine}`
                  : 'Sin déficit conocido',
            },
            {
              title: 'Cobertura de horas',
              value: `${num(coveragePct)} %`,
              helper: `${capacity.summary.missing_hours_orders} OF sin horas`,
            },
          ].map((card) => (
            <Paper
              key={card.title}
              elevation={0}
              sx={{ ...panelSx, p: 1.35 }}
            >
              <Typography
                sx={{ color: '#94a3b8', fontSize: '0.72rem' }}
              >
                {card.title}
              </Typography>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '1.35rem',
                  fontWeight: 900,
                }}
              >
                {card.value}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#64748b' }}
              >
                {card.helper}
              </Typography>
            </Paper>
          ))}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <TableCell>Línea</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Evolución · 4 semanas</TableCell>
                <TableCell>Oportunidad</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedMachines.map((machine) => {
                const pressure = machine.pressure;
                const presentation =
                  getPressurePresentation(machine);
                const expanded =
                  expandedPressureMachine === machine.machine;

                const sourceConfig =
                  configuredLineByKey.get(
                    normalizeLineKey(machine.machine),
                  );
                const sourceGroupId =
                  sourceConfig?.production_line_group_id ?? null;
                const sourceGroupLabel =
                  sourceConfig?.production_line_group_name
                  || sourceConfig?.production_line_group_code
                  || null;

                const candidates =
                  pressure.receiver_candidates
                    .filter((candidate) => {
                      if (!sourceGroupId) return false;

                      const candidateConfig =
                        configuredLineByKey.get(
                          normalizeLineKey(candidate.machine),
                        );

                      return (
                        candidateConfig?.production_line_group_id
                        === sourceGroupId
                      );
                    })
                    .slice(0, 2);

                const pressureOrders =
                  pressure.peak_pressure_orders.slice(0, 20);

                return (
                  <Fragment key={`pressure-${machine.machine}`}>
                    <TableRow>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography
                          sx={{
                            color: '#7dd3fc',
                            fontWeight: 900,
                          }}
                        >
                          {machine.machine}
                        </Typography>

                        {sourceGroupLabel && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              display: 'block',
                            }}
                          >
                            Grupo {sourceGroupLabel}
                          </Typography>
                        )}

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#94a3b8',
                            display: 'block',
                          }}
                        >
                          {num(machine.known_pending_hours)} h de carga
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ minWidth: 250 }}>
                        <Stack spacing={0.35} alignItems="flex-start">
                          <Chip
                            size="small"
                            color={
                              presentation.hasPressure
                                ? 'error'
                                : 'default'
                            }
                            variant="outlined"
                            label={presentation.label}
                          />

                          {presentation.hasPressure ? (
                            <Typography
                              sx={{
                                color: '#fca5a5',
                                fontSize: '0.8rem',
                                fontWeight: 900,
                              }}
                            >
                              Déficit máx.{' '}
                              {num(pressure.peak_deficit_hours)} h ·{' '}
                              {displayDate(pressure.peak_deficit_date)}
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: '#94a3b8' }}
                            >
                              {presentation.detail}
                            </Typography>
                          )}

                          {presentation.hasPressure
                            && presentation.recoveryDate && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8' }}
                              >
                                Recupera margen desde{' '}
                                {displayDate(
                                  presentation.recoveryDate,
                                )}
                              </Typography>
                            )}

                          {presentation.hasPressure
                            && !presentation.recoveryDate && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8' }}
                              >
                                {presentation.detail}
                              </Typography>
                            )}

                          {machine.overdue_orders > 0 ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#64748b' }}
                            >
                              {machine.overdue_orders} OF ya atrasadas ·{' '}
                              {num(machine.overdue_known_hours)} h
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: '#64748b' }}
                            >
                              Sin atraso previo
                            </Typography>
                          )}

                          {machine.missing_hours_orders > 0 && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#fbbf24' }}
                            >
                              {machine.missing_hours_orders} OF sin horas ·
                              lectura parcial
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ minWidth: 370 }}>
                        <PressureTimeline
                          windows={machine.pressure_windows}
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 360 }}>
                        <Stack spacing={0.55} alignItems="flex-start">
                          {!presentation.hasPressure ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#94a3b8' }}
                            >
                              No requiere revisión de capacidad en este
                              horizonte.
                            </Typography>
                          ) : sourceGroupId && candidates.length > 0 ? (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ color: '#cbd5e1' }}
                              >
                                Revisar redistribución dentro del grupo{' '}
                                <strong>
                                  {sourceGroupLabel || 'operativo'}
                                </strong>
                                :
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.6}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {candidates.map((candidate) => (
                                  <Chip
                                    key={`${machine.machine}-${candidate.machine}`}
                                    size="small"
                                    variant="outlined"
                                    label={`${candidate.machine} · +${num(
                                      candidate.known_margin_hours,
                                    )} h`}
                                    sx={{
                                      color: '#cbd5e1',
                                      borderColor:
                                        'rgba(148,163,184,0.28)',
                                    }}
                                  />
                                ))}
                              </Stack>

                              <Typography
                                variant="caption"
                                sx={{ color: '#64748b' }}
                              >
                                Falta confirmar compatibilidad de referencia.
                              </Typography>
                            </>
                          ) : sourceGroupId ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#94a3b8' }}
                            >
                              No hay otra línea del grupo con margen conocido
                              en el pico de presión.
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: '#fbbf24' }}
                            >
                              Sin grupo operativo configurado: no se buscan
                              alternativas entre líneas.
                            </Typography>
                          )}

                          {presentation.hasPressure && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                setExpandedPressureMachine(
                                  expanded
                                    ? null
                                    : machine.machine,
                                )
                              }
                              sx={{
                                minWidth: 0,
                                p: 0,
                                textTransform: 'none',
                                fontSize: '0.72rem',
                                fontWeight: 850,
                              }}
                            >
                              {expanded
                                ? 'Ocultar análisis'
                                : 'Analizar sobrecarga'}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {expanded && presentation.hasPressure && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            bgcolor:
                              'rgba(15,23,42,0.44)',
                            py: 1.5,
                          }}
                        >
                          <Stack spacing={1.4}>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md:
                                    'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 1,
                              }}
                            >
                              {[
                                {
                                  label:
                                    'Deben estar fabricadas',
                                  value: `${num(
                                    pressure.peak_required_hours
                                      ?? 0,
                                  )} h`,
                                },
                                {
                                  label:
                                    'Capacidad disponible',
                                  value: `${num(
                                    pressure
                                      .peak_available_capacity_hours
                                      ?? 0,
                                  )} h`,
                                },
                                {
                                  label: 'Déficit conocido',
                                  value: `${num(
                                    pressure
                                      .peak_deficit_hours,
                                  )} h`,
                                },
                              ].map((item) => (
                                <Paper
                                  key={item.label}
                                  elevation={0}
                                  sx={{
                                    ...panelSx,
                                    p: 1.2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: '0.7rem',
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontSize: '1.15rem',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {item.value}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#64748b' }}
                                  >
                                    Al{' '}
                                    {displayDate(
                                      pressure
                                        .peak_deficit_date,
                                    )}
                                  </Typography>
                                </Paper>
                              ))}
                            </Box>

                            {sourceGroupId ? (
                              candidates.length > 0 ? (
                                <Alert
                                  severity="info"
                                  variant="outlined"
                                >
                                  Dentro del grupo{' '}
                                  <strong>
                                    {sourceGroupLabel
                                      || 'operativo'}
                                  </strong>{' '}
                                  existen líneas con margen en el
                                  mismo punto:{' '}
                                  {candidates.map(
                                    (candidate, index) => (
                                      <Fragment
                                        key={`detail-${machine.machine}-${candidate.machine}`}
                                      >
                                        {index > 0 ? ', ' : ''}
                                        <strong>
                                          {candidate.machine}
                                        </strong>{' '}
                                        (+{num(
                                          candidate
                                            .known_margin_hours,
                                        )} h)
                                      </Fragment>
                                    ),
                                  )}
                                  . Esto solo indica dónde merece
                                  la pena revisar. La referencia
                                  concreta todavía debe confirmarse
                                  como compatible.
                                </Alert>
                              ) : (
                                <Alert
                                  severity="info"
                                  variant="outlined"
                                >
                                  No hay otra línea del grupo{' '}
                                  <strong>
                                    {sourceGroupLabel
                                      || 'operativo'}
                                  </strong>{' '}
                                  con margen conocido en el pico de
                                  presión.
                                </Alert>
                              )
                            ) : (
                              <Alert
                                severity="warning"
                                variant="outlined"
                              >
                                No se evalúan alternativas porque
                                esta línea no tiene grupo operativo
                                configurado.
                              </Alert>
                            )}

                            <Box>
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 900,
                                }}
                              >
                                OF futuras implicadas hasta el pico
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8' }}
                              >
                                El atraso anterior ya está incluido
                                en las horas necesarias. Estas son
                                las OF futuras cuyo límite de
                                fabricación cae hasta ese punto.
                              </Typography>
                            </Box>

                            {pressureOrders.length > 0 ? (
                              <TableContainer
                                sx={{
                                  maxHeight: 360,
                                  overflowY: 'auto',
                                }}
                              >
                                <Table
                                  size="small"
                                  stickyHeader
                                  sx={{ minWidth: 820 }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>OF</TableCell>
                                      <TableCell>
                                        Referencia
                                      </TableCell>
                                      <TableCell align="right">
                                        Horas
                                      </TableCell>
                                      <TableCell>
                                        Límite fabricación
                                      </TableCell>
                                      <TableCell>
                                        Expedición
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {pressureOrders.map(
                                      (order) => (
                                        <TableRow
                                          key={`${machine.machine}-pressure-${order.manufacturing_order_number}`}
                                        >
                                          <TableCell>
                                            {
                                              order
                                                .manufacturing_order_number
                                            }
                                          </TableCell>
                                          <TableCell>
                                            {order.product_ref
                                              || '—'}
                                          </TableCell>
                                          <TableCell
                                            align="right"
                                          >
                                            {order.pending_hours
                                              === null
                                              ? 'Sin horas'
                                              : `${num(
                                                  order
                                                    .pending_hours,
                                                )} h`}
                                          </TableCell>
                                          <TableCell>
                                            {displayDate(
                                              order
                                                .manufacturing_deadline,
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {displayDate(
                                              order
                                                .expedition_date,
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Alert
                                severity="info"
                                variant="outlined"
                              >
                                El déficit del pico procede del
                                atraso que ya existía; todavía no
                                se han añadido nuevas OF futuras
                                hasta ese punto.
                              </Alert>
                            )}

                            {pressure.peak_pressure_orders.length
                              > 20 && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#64748b' }}
                              >
                                Se muestran las primeras 20 de{' '}
                                {
                                  pressure
                                    .peak_pressure_orders.length
                                }{' '}
                                OF futuras implicadas.
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          sx={{ color: '#64748b', fontSize: '0.72rem' }}
        >
          La banda representa presión acumulada, no ocupación
          instantánea. Una semana sin déficit significa que la carga
          conocida que debe estar terminada hasta esas fechas cabe
          dentro de la capacidad acumulada. Las oportunidades entre
          líneas se limitan al mismo grupo operativo, pero la
          compatibilidad de cada referencia todavía requiere
          confirmación.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.16)' }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Simulación de orden · experimental
            </Typography>
            <Typography
              sx={{ color: '#94a3b8', fontSize: '0.76rem', mt: 0.2 }}
            >
              Se conserva como herramienta secundaria para explorar si el
              orden puede cambiar el resultado. No representa una instrucción
              de planificación.
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              setShowSequenceSimulator((current) => !current)
            }
            sx={{ textTransform: 'none', fontWeight: 850 }}
          >
            {showSequenceSimulator
              ? 'Ocultar simulación'
              : 'Ver simulación de orden'}
          </Button>
        </Box>

        {showSequenceSimulator && (
          <Stack spacing={1.8}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              xl: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            {
              title: 'Ya fuera de plazo',
              value: `${capacity.summary.overdue_orders_count} OF`,
              helper: 'Límite de fabricación ya superado',
            },
            {
              title: 'Todavía salvables',
              value: `${calculableFutureOrders} OF`,
              helper: 'Futuras con horas y fecha calculables',
            },
            {
              title: 'Orden simple por fecha',
              value: `${baselineFutureLateOrders} OF tarde`,
              helper: 'Nuevas tardanzas que aparecerían',
            },
            {
              title: 'Reordenando con PROA',
              value: `${proposedFutureLateOrders} OF tarde`,
              helper: 'Nuevas tardanzas que seguirían quedando',
            },
            {
              title: 'Mejora potencial',
              value: `${savedFutureOrders} OF`,
              helper: 'Nuevas tardanzas evitadas',
            },
          ].map((card) => (
            <Paper
              key={card.title}
              elevation={0}
              sx={{ ...panelSx, p: 1.4 }}
            >
              <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                {card.title}
              </Typography>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '1.45rem',
                  fontWeight: 900,
                }}
              >
                {card.value}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {card.helper}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.16)' }} />

        <Box>
          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
            Decisión por línea
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.76rem', mt: 0.25 }}>
            Lee cada fila de izquierda a derecha: qué ya está vencido,
            cuántas nuevas tardanzas aparecerían con un orden simple por fecha,
            cuántas quedarían tras reordenar y qué coste tiene esa decisión
            sobre el backlog que ya venía tarde.
          </Typography>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1260 }}>
            <TableHead>
              <TableRow>
                <TableCell>Línea</TableCell>
                <TableCell>Lo que ya está fuera de plazo</TableCell>
                <TableCell>Si solo ordeno por fecha</TableCell>
                <TableCell>Si aplico la propuesta PROA</TableCell>
                <TableCell>Qué significa</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedMachines.map((machine) => {
                const scheduling = machine.scheduling;
                const baseline = scheduling.baseline;
                const proposed = scheduling.proposed;
                const improvement = scheduling.improvement;
                const decision = scheduleDecision(machine);
                const expanded = expandedMachine === machine.machine;
                const futureCalculableOrders = Math.max(
                  scheduling.eligible_orders
                  - scheduling.already_overdue_orders,
                  0,
                );
                const savedOrders =
                  improvement?.saved_future_late_orders ?? 0;
                const baselineBacklogFinish =
                  scenarioBacklogCompletionDate(baseline);
                const proposedBacklogFinish =
                  scenarioBacklogCompletionDate(proposed);
                const partialCoverage =
                  scheduling.excluded_missing_hours > 0
                  || scheduling.excluded_missing_date > 0;

                const baselinePositions = new Map(
                  (baseline?.sequence ?? []).map((order) => [
                    order.manufacturing_order_number,
                    order.position,
                  ]),
                );

                return (
                  <Fragment key={machine.machine}>
                    <TableRow>
                      <TableCell>
                        <Typography sx={{ color: '#7dd3fc', fontWeight: 900 }}>
                          {machine.machine}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#94a3b8', display: 'block' }}
                        >
                          {machine.orders_count} OF ·{' '}
                          {num(machine.known_pending_hours)} h conocidas
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: partialCoverage ? '#fbbf24' : '#64748b',
                            display: 'block',
                          }}
                        >
                          {partialCoverage
                            ? `${scheduling.excluded_missing_hours} sin horas · ${scheduling.excluded_missing_date} sin fecha`
                            : 'Cobertura completa para la simulación'}
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() =>
                            setExpandedMachine(
                              expanded ? null : machine.machine,
                            )
                          }
                          sx={{
                            minWidth: 0,
                            p: 0,
                            mt: 0.3,
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: 800,
                          }}
                        >
                          {expanded ? 'Ocultar orden' : 'Ver orden propuesto'}
                        </Button>
                      </TableCell>

                      <TableCell>
                        {machine.overdue_orders > 0 ? (
                          <>
                            <Typography sx={{ fontWeight: 900, color: '#fbbf24' }}>
                              {machine.overdue_orders} OF ya vencidas
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#cbd5e1', display: 'block' }}
                            >
                              {num(machine.overdue_known_hours)} h conocidas de
                              trabajo que ya debería estar terminado
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography sx={{ color: '#86efac', fontWeight: 900 }}>
                              0 OF vencidas
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#64748b', display: 'block' }}
                            >
                              No arrastra atraso previo
                            </Typography>
                          </>
                        )}
                      </TableCell>

                      <TableCell>
                        {baseline ? (
                          <>
                            <Typography
                              sx={{
                                fontWeight: 900,
                                color:
                                  baseline.future_late_orders > 0
                                    ? '#fca5a5'
                                    : '#86efac',
                              }}
                            >
                              {baseline.future_late_orders} de{' '}
                              {futureCalculableOrders} futuras acabarían tarde
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: '#94a3b8', display: 'block' }}
                            >
                              Si solo se prioriza la fecha límite
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ color: '#94a3b8' }}>
                            Sin cálculo
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {proposed ? (
                          <>
                            <Typography
                              sx={{
                                fontWeight: 900,
                                color:
                                  proposed.future_late_orders > 0
                                    ? '#fca5a5'
                                    : '#86efac',
                              }}
                            >
                              {proposed.future_late_orders} de{' '}
                              {futureCalculableOrders} futuras acabarían tarde
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: savedOrders > 0 ? '#86efac' : '#94a3b8',
                                display: 'block',
                                fontWeight: savedOrders > 0 ? 800 : 400,
                              }}
                            >
                              {savedOrders > 0
                                ? `${savedOrders} nuevas tardanzas evitadas`
                                : 'No mejora el número de nuevas tardanzas'}
                            </Typography>
                          </>
                        ) : (
                          <Typography sx={{ color: '#fbbf24' }}>
                            {scheduling.reason || 'Sin cálculo'}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.45} alignItems="flex-start">
                          <Chip
                            size="small"
                            color={decision.color}
                            label={decision.label}
                          />

                          {proposed?.future_late_orders ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#cbd5e1' }}
                            >
                              Aun reordenando, quedan{' '}
                              {proposed.future_late_orders} OF futuras que no
                              caben con la capacidad disponible.
                            </Typography>
                          ) : savedOrders > 0 && machine.overdue_orders > 0 ? (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ color: '#cbd5e1' }}
                              >
                                PROA protege las OF todavía salvables dejando
                                parte del backlog ya vencido para después.
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: '#94a3b8' }}
                              >
                                Última OF vencida calculable con PROA:{' '}
                                <strong>
                                  {displayDate(proposedBacklogFinish)}
                                </strong>
                                {baselineBacklogFinish
                                  && proposedBacklogFinish
                                  && baselineBacklogFinish !== proposedBacklogFinish
                                  ? ` · por fecha: ${displayDate(baselineBacklogFinish)}`
                                  : ''}
                              </Typography>
                            </>
                          ) : machine.overdue_orders > 0 ? (
                            <Typography
                              variant="caption"
                              sx={{ color: '#cbd5e1' }}
                            >
                              No se crean nuevas tardanzas futuras; el problema
                              que queda es el backlog ya vencido.
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{ color: '#cbd5e1' }}
                            >
                              Toda la carga calculable cabe dentro de sus
                              límites de fabricación.
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {expanded && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{
                            bgcolor: 'rgba(15,23,42,0.44)',
                            py: 1.5,
                          }}
                        >
                          <Stack spacing={1.4}>
                            <Alert
                              severity={
                                savedOrders > 0 && machine.overdue_orders > 0
                                  ? 'warning'
                                  : 'info'
                              }
                              variant="outlined"
                            >
                              {savedOrders > 0 && machine.overdue_orders > 0
                                ? `Para evitar ${savedOrders} nuevas tardanzas, PROA está adelantando compromisos todavía salvables y dejando algunas OF ya vencidas para después. El backlog no desaparece: cambia su posición en la secuencia.`
                                : 'Esta propuesta solo reordena OF dentro de la misma línea. No mueve referencias a otra máquina y no modifica la planificación real.'}
                            </Alert>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  md: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 1,
                              }}
                            >
                              <ScenarioMetric
                                title="Orden simple por fecha"
                                lateOrders={
                                  baseline?.future_late_orders ?? null
                                }
                                tardinessHours={
                                  baseline?.future_tardiness_hours ?? null
                                }
                                helper="Referencia simple: fabricar primero lo que antes debe estar terminado."
                                accent={
                                  baseline?.future_late_orders
                                    ? 'warning'
                                    : 'success'
                                }
                              />
                              <ScenarioMetric
                                title="Propuesta PROA"
                                lateOrders={
                                  proposed?.future_late_orders ?? null
                                }
                                tardinessHours={
                                  proposed?.future_tardiness_hours ?? null
                                }
                                helper="Reordena para evitar el mayor número posible de nuevas tardanzas."
                                accent={
                                  proposed?.future_late_orders
                                    ? 'warning'
                                    : 'success'
                                }
                              />
                              <Paper
                                elevation={0}
                                sx={{ ...panelSx, p: 1.4, minHeight: 104 }}
                              >
                                <Typography
                                  sx={{ color: '#94a3b8', fontSize: '0.72rem' }}
                                >
                                  Qué pasa con el backlog
                                </Typography>
                                <Typography
                                  sx={{
                                    color: machine.overdue_orders > 0
                                      ? '#fbbf24'
                                      : '#86efac',
                                    fontSize: '1.32rem',
                                    fontWeight: 900,
                                  }}
                                >
                                  {machine.overdue_orders > 0
                                    ? `${machine.overdue_orders} OF ya vencidas`
                                    : 'Sin backlog'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#94a3b8', display: 'block' }}
                                >
                                  {machine.overdue_orders > 0
                                    ? `Última OF vencida calculable con PROA: ${displayDate(proposedBacklogFinish)}`
                                    : 'No hay OF vencidas que desplazar.'}
                                </Typography>
                                {baselineBacklogFinish
                                  && proposedBacklogFinish
                                  && baselineBacklogFinish !== proposedBacklogFinish && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: '#64748b', display: 'block' }}
                                    >
                                      Con orden por fecha: {' '}
                                      {displayDate(baselineBacklogFinish)}
                                    </Typography>
                                  )}
                              </Paper>
                            </Box>

                            <Box>
                              <Typography
                                sx={{ color: '#f8fafc', fontWeight: 900 }}
                              >
                                Orden de fabricación que propone PROA
                              </Typography>
                              <Typography
                                sx={{
                                  color: '#94a3b8',
                                  fontSize: '0.74rem',
                                  mt: 0.2,
                                }}
                              >
                                El movimiento de posición se compara contra el
                                orden simple por fecha; no representa la
                                planificación real actualmente guardada.
                              </Typography>
                            </Box>

                            {proposed ? (
                              <TableContainer
                                sx={{ maxHeight: 420, overflowY: 'auto' }}
                              >
                                <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Orden propuesto</TableCell>
                                      <TableCell>OF</TableCell>
                                      <TableCell>Referencia</TableCell>
                                      <TableCell align="right">Horas</TableCell>
                                      <TableCell>Expedición</TableCell>
                                      <TableCell>Límite fabricación</TableCell>
                                      <TableCell>Final estimado</TableCell>
                                      <TableCell>Resultado</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {proposed.sequence.map((order) => {
                                      const result = orderResult(order);
                                      const oldPosition =
                                        baselinePositions.get(
                                          order.manufacturing_order_number,
                                        );

                                      return (
                                        <TableRow
                                          key={`${machine.machine}-${order.manufacturing_order_number}-${order.position}`}
                                        >
                                          <TableCell>
                                            <Typography sx={{ fontWeight: 850 }}>
                                              #{order.position}
                                            </Typography>
                                            {oldPosition !== undefined
                                              && oldPosition !== order.position && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: '#64748b',
                                                    display: 'block',
                                                  }}
                                                >
                                                  por fecha #{oldPosition}
                                                </Typography>
                                              )}
                                          </TableCell>
                                          <TableCell>
                                            {order.manufacturing_order_number}
                                          </TableCell>
                                          <TableCell>
                                            {order.product_ref || '—'}
                                          </TableCell>
                                          <TableCell align="right">
                                            {num(order.pending_hours)} h
                                          </TableCell>
                                          <TableCell>
                                            {displayDate(order.expedition_date)}
                                            {order.date_source === 'theoretical'
                                              ? ' · Teórica'
                                              : ''}
                                          </TableCell>
                                          <TableCell>
                                            {displayDate(
                                              order.manufacturing_deadline,
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {displayDate(order.completion_date)}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              color={result.color}
                                              label={result.label}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Alert severity="warning" variant="outlined">
                                {scheduling.reason
                                  || 'No se puede construir una secuencia con los datos disponibles.'}
                              </Alert>
                            )}

                            {(scheduling.excluded_missing_hours > 0
                              || scheduling.excluded_missing_date > 0) && (
                              <Alert severity="warning" variant="outlined">
                                La simulación excluye{' '}
                                {scheduling.excluded_missing_hours} OF sin horas
                                y {scheduling.excluded_missing_date} OF sin fecha.
                                La conclusión es parcial.
                              </Alert>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ borderColor: 'rgba(148,163,184,0.16)' }} />

        <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
          Hipótesis de esta primera simulación: una máquina por línea, todas las
          OF calculables disponibles desde la fecha de análisis, duración
          determinista y capacidad diaria completa. Todavía no se modelan
          anclas de planificación, preparaciones dependientes de secuencia,
          paradas futuras, festivos/cierres ni traslados entre máquinas. Si la
          fecha de análisis es hoy, se considera disponible el día productivo
          completo; la hora actual se incorporará en una evolución posterior.
        </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
